<script setup lang="ts">
import { ref, watch } from 'vue'
import {
	Dialog,
	DialogContent,
	DialogClose,
	DialogTitle,
	DialogDescription,
} from '@/components/ui/dialog'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'

interface SortOption {
	id: string
	label: string
	value?: string
}

const props = defineProps({
	isOpen: {
		type: Boolean,
		default: false,
	},
	sortOptions: {
		type: Array as () => SortOption[],
		required: true,
		default: () => [],
	},
	defaultValue: {
		type: String,
		default: 'recommended',
	},
	filterContent: {
		type: Object,
		required: true,
	},
})

const emit = defineEmits(['update:isOpen', 'apply'])

const selectedSort = ref(props.defaultValue)

watch(
	() => props.defaultValue,
	newValue => {
		selectedSort.value = newValue
	},
)

watch(
	() => props.isOpen,
	isOpen => {
		if (isOpen) {
			selectedSort.value = props.defaultValue
		}
	},
)

const handleApply = () => {
	emit('apply', selectedSort.value)
	emit('update:isOpen', false)
}

const handleClose = () => {
	emit('update:isOpen', false)
}
</script>

<template>
	<Dialog
		:open="isOpen"
		@update:open="(value: boolean) => emit('update:isOpen', value)"
	>
		<DialogContent class="w-[350px] rounded-lg border-0 bg-white p-0">
			<div class="flex flex-col">
				<header
					class="flex items-center justify-between border-b border-n-200 px-24px py-16px"
				>
					<DialogTitle class="typography-h5-str text-n-950">
						{{ props.filterContent.sort.title }}
					</DialogTitle>
					<DialogClose
						@click="handleClose"
						class="flex h-24px w-24px items-center justify-center"
					>
						<BaseIcon name="close" class="h-20px w-20px" />
					</DialogClose>
				</header>

				<main class="py-16px">
					<RadioGroup v-model="selectedSort" class="space-y-0">
						<div
							v-for="option in sortOptions"
							:key="option.id"
							class="group cursor-pointer px-24px py-16px"
							@click="selectedSort = option.id"
						>
							<div class="flex items-center justify-between">
								<label
									:for="option.id"
									class="typography-s1-reg cursor-pointer select-none text-n-950"
								>
									{{ option.label }}
								</label>
								<RadioGroupItem :value="option.id" :id="option.id" />
							</div>
						</div>
					</RadioGroup>
				</main>

				<footer class="flex w-full justify-end p-16px">
					<BaseButton
						variant="primary"
						size="medium"
						class="w-auto"
						@click="handleApply"
					>
						{{ props.filterContent.buttons.apply }}
					</BaseButton>
				</footer>
			</div>
		</DialogContent>
	</Dialog>
</template>
